import Router from 'vue-router';

import store from './store/index';

import MainLayout from './views/MainLayout.vue';
import PageNotFound from './views/PageNotFound.vue';

import ActivateAccountRoutes from './routes/activate-account';
import AddMemberRoutes from './routes/add-member';
import AddResultsRoutes from './routes/add-results';
import AdminRoutes from './routes/admin';
import AuthRoutes, { logout } from './routes/auth';
import CalendarRoutes from './routes/calendar';
import ChallengesRoutes from './routes/challenges';
import ChallengesPublic from './routes/challenges-public';
import ClubMembersRoutes from './routes/club-members';
import CreateClubRoutes from './routes/create-club';
import CreateEventRoutes from './routes/create-event';
import CreateSessionRoutes from './routes/create-session';
import CrewsRoutes from './routes/crews';
import EventRoutes from './routes/event';
// hide equipment routes
import EquipmentListRoutes from './routes/equipment-list';
import InvitationRoutes from './routes/invitation';
import HandlePaymentRoutes from './routes/handle-payment';
import HomeRoutes from './routes/home';
import NotificationsRoutes from './routes/notifications';
import ProfileRoutes from './routes/profile';
import ReportsRoutes from './routes/reports';
import SearchResultsRoutes from './routes/search-results';
import SessionRoutes from './routes/session';
import SettingsRoutes from './routes/settings';
import StyleguideRoutes from './routes/styleguide';
import SquadsRoutes from './routes/squads';
import UnauthorizedRoutes from './routes/unauthorized';
import UpgradeToPerformanceRoutes from './routes/upgrade-to-performance';
import WelcomeRoutes from './routes/welcome';

import {
    ROUTES_CREATE_CLUB,
    ROUTES_HOME,
    ROUTES_LOGIN,
    ROUTES_LOGOUT,
    ROUTES_SETTINGS_MY_CLUBS,
    ROUTES_SETTINGS_BILLING,
    ROUTES_SETTINGS_BILLING_LICENCE_EXPIRED,
    ROUTES_WELCOME
} from './constants/routes';
import { LUDUM_ERROR_CODES } from '@/constants';
import { LudumEventBus, CLOSE_SEARCH, CLOSE_MENU_DROPDOWN } from '@/event-bus';
import get from 'get-object-path';

Vue.use(Router);

const routerConfig = {
    routes: [
        {
            path: '/dashboard',
            redirect: '/app/home'
        },
        ChallengesPublic,
        {
            path: '/',
            redirect: '/home',
            component: MainLayout,
            meta: {
                requiresAuth: true
            },
            children: [
                AddMemberRoutes,
                AddResultsRoutes,
                CalendarRoutes,
                ChallengesRoutes,
                ClubMembersRoutes,
                CreateClubRoutes,
                CreateEventRoutes,
                CreateSessionRoutes,
                CrewsRoutes,
                EventRoutes,
                HomeRoutes,
                NotificationsRoutes,
                ProfileRoutes,
                ReportsRoutes,
                SearchResultsRoutes,
                SessionRoutes,
                SettingsRoutes,
                StyleguideRoutes,
                SquadsRoutes,
                UnauthorizedRoutes,
                UpgradeToPerformanceRoutes,
                WelcomeRoutes
            ]
        },
        AuthRoutes,
        AdminRoutes,
        ActivateAccountRoutes,
        HandlePaymentRoutes,
        InvitationRoutes,
        logout,
        { path: '*', component: PageNotFound }
    ],
    mode: 'history'
};

const router = new Router(routerConfig);

router.afterEach(function(to, from, next) {
    localStorage.setItem('ludum_last_route', from.name);
});

router.beforeEach(function(to, from, next) {
    store.dispatch('closeAllModals');
    LudumEventBus.$emit(CLOSE_SEARCH);
    LudumEventBus.$emit(CLOSE_MENU_DROPDOWN);

    if (to.matched.some(record => record.name === ROUTES_LOGOUT)) {
        store.dispatch('emptyClubs', []);
        Vue.auth.destroyToken();
        Vue.cookie.delete('selectedClub');
        localStorage.clear();
        return next({ name: ROUTES_LOGIN });
    }

    const isAuthenticated = Vue.auth.isAuthenticated();
    if (isAuthenticated && to.matched.some(record => record.meta.guest)) {
        return next('/');
    }
    if (to.matched.some(record => record.meta.requiresAuth)) {
        if (isAuthenticated) {
            return store.dispatch('clubHelper/getMe').then(async results => {
                await store.dispatch('fetchUserClubs');
                await store.dispatch('ensureClubIsSelected');

                // check user admin flag if route is admin
                if (to.matched.some(record => record.meta.requiresAdmin) && !results.data.is_admin) {
                    next({ name: ROUTES_HOME });
                }

                const redirectionState = await defineRedirectionState(to.name);

                if (redirectionState) {
                    return next({ name: redirectionState });
                }

                return next();
            });
        }

        saveGoToRoute(from, to);

        return next({
            name: ROUTES_LOGIN
        });
    } else {
        return next();
    }
});

function saveGoToRoute(from, to) {
    if (![ROUTES_LOGIN, ROUTES_HOME].includes(to.name)) {
        localStorage.setItem(
            'ludumGoToState',
            JSON.stringify({
                name: to.name,
                params: to.params
            })
        );
    }
}

// check if user has 419 (no clubs) or 420 (subscription expired) in his responses and redirect to appropriate pages
async function defineRedirectionState(toState) {
    const tokenCheckResponse = await store.dispatch('checkToken');
    const errorCode = get(tokenCheckResponse, 'response.data.status_code');
    await store.dispatch('updateErrorCode', errorCode);

    const route = store.getters.hasBillingPermissionInSelectedClub
        ? ROUTES_SETTINGS_BILLING
        : ROUTES_SETTINGS_BILLING_LICENCE_EXPIRED;
    if (errorCode === LUDUM_ERROR_CODES.SUBSCRIPTION_EXPIRED && ![ROUTES_SETTINGS_MY_CLUBS, route].includes(toState)) {
        return route;
    }

    if (errorCode === LUDUM_ERROR_CODES.NO_CLUBS && ![ROUTES_CREATE_CLUB, ROUTES_WELCOME].includes(toState)) {
        return ROUTES_WELCOME;
    }

    if (errorCode !== LUDUM_ERROR_CODES.NO_CLUBS && toState === ROUTES_WELCOME) {
        return ROUTES_HOME;
    }
}

export default router;
